import { useCallback, useEffect, type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import Close from 'dibs-icons/exports/Close.ts';
import { defineMessages, useIntl } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';

import { SbRespRefineMenuSelector } from '../SbRespRefineMenuSelector/SbRespRefineMenuSelector.tsx';
import { SbSharedRefineMenuFooter } from '../SbSharedRefineMenuFooter/SbSharedRefineMenuFooter.tsx';
import { useSbSelector } from '../../reducers/useSbSelector.ts';
import { useRefineMenuClose } from '../SbSharedRefineMenu/hooks/useRefineMenuClose.ts';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';

import styles from './SbRespRefineMenuDrawer.module.css';
import dibsCss from 'dibs-css';

import { type SbRespRefineMenuDrawer_viewer$key } from './__generated__/SbRespRefineMenuDrawer_viewer.graphql.ts';
import { type SbRespRefineMenuDrawer_itemSearch$key } from './__generated__/SbRespRefineMenuDrawer_itemSearch.graphql.ts';
import { type SbRespRefineMenuDrawer_user$key } from './__generated__/SbRespRefineMenuDrawer_user.graphql.ts';

const messages = defineMessages({
    close: { id: 'abf.SbRespRefineMenuDrawer.close', defaultMessage: 'Close' },
    filters: { id: 'abf.SbRespRefineMenuDrawer.filters', defaultMessage: 'Filters' },
});

const viewerFragment = graphql`
    fragment SbRespRefineMenuDrawer_viewer on Viewer {
        ...SbRespRefineMenuSelector_viewer
    }
`;
const itemSearchFragment = graphql`
    fragment SbRespRefineMenuDrawer_itemSearch on ItemSearchQueryConnection {
        ...SbRespRefineMenuSelector_itemSearch
        ...sbSharedAppliedFiltersHelpers_itemSearch
        ...SbSharedRefineMenuFooter_itemSearch
        appliedFilters {
            ...useRefineMenuClose_appliedFilters
        }
    }
`;

const userFragment = graphql`
    fragment SbRespRefineMenuDrawer_user on User {
        ...SbRespRefineMenuSelector_user
    }
`;

type Props = {
    itemSearch: SbRespRefineMenuDrawer_itemSearch$key;
    viewer: SbRespRefineMenuDrawer_viewer$key;
    user: SbRespRefineMenuDrawer_user$key | null | undefined;
    currency: string;
    isThinPage: boolean;
};

export const SbRespRefineMenuDrawer: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
    user: userRef,
    currency,
    isThinPage,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const user = useFragment(userFragment, userRef);
    const intl = useIntl();
    const afterDisplay = useSbSelector(state => state.relayVariables.variables.afterDisplay);
    const { generatedUriRef, filtersShown } = useSbSelector(state => state.filters);

    const handleRefineMenuClose = useRefineMenuClose(
        (itemSearch?.appliedFilters || []).filter(filterNulls)
    );
    const onClose = useCallback(() => {
        handleRefineMenuClose({ areFiltersApplied: true });
    }, [handleRefineMenuClose]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent): void => {
            if (e.key === 'Escape' || e.key === 'Esc') {
                e.preventDefault();
                onClose();
            }
        };
        if (filtersShown) {
            window.addEventListener('keydown', handleKeyDown);
            window.document.body.classList.add(dibsCss.overflowHidden);

            return () => {
                window.removeEventListener('keydown', handleKeyDown);
                window.document.body.classList.remove(dibsCss.overflowHidden);
            };
        }
        return () => {};
    }, [filtersShown, onClose]);

    return (
        <div
            className={classnames(
                styles.overlay,
                dibsCss.fixed,
                dibsCss.top0,
                dibsCss.left0,
                dibsCss.wFull,
                dibsCss.hFull,
                dibsCss.flex,
                dibsCss.zOverlay,
                { [styles.overlayHidden]: !filtersShown }
            )}
            data-tn="refine-menu-drawer"
            aria-hidden={!filtersShown}
        >
            <div
                className={classnames(
                    styles.filterContainer,
                    dibsCss.hFull,
                    dibsCss.bgWhite,
                    dibsCss.flex,
                    dibsCss.flexCol,
                    dibsCss.relative,
                    { [styles.filterContainerOpen]: filtersShown }
                )}
            >
                <div
                    className={classnames(
                        dibsCss.textHeaderLargeNormal,
                        dibsCss.mtLarge,
                        dibsCss.mxLarge,
                        dibsCss.mbMedium
                    )}
                >
                    {intl.formatMessage(messages.filters)}
                </div>
                <Link
                    className={classnames(
                        styles.closeCta,
                        dibsCss.absolute,
                        dibsCss.top0,
                        dibsCss.right0
                    )}
                    onClick={onClose}
                    ariaLabel={intl.formatMessage(messages.close)}
                >
                    <Close variation="24" />
                </Link>
                <div
                    className={classnames(
                        styles.menu,
                        dibsCss.flex1,
                        dibsCss.overflowYAuto,
                        dibsCss.mlLarge
                    )}
                >
                    <SbRespRefineMenuSelector
                        viewer={viewer}
                        itemSearch={itemSearch}
                        user={user}
                        currency={currency}
                        isClient={afterDisplay}
                        uriRef={generatedUriRef || ''}
                        isThinPage={isThinPage}
                    />
                </div>
                <div
                    className={classnames(
                        styles.footerContainer,
                        dibsCss.absolute,
                        dibsCss.insetX0,
                        dibsCss.bottom0,
                        dibsCss.wFull,
                        dibsCss.zHigh,
                        dibsCss.flex,
                        dibsCss.pxSmall,
                        dibsCss.itemsCenter
                    )}
                >
                    <SbSharedRefineMenuFooter
                        itemSearch={itemSearch}
                        hideRefineMenu={handleRefineMenuClose}
                    />
                </div>
            </div>
            <div
                className={classnames(dibsCss.wFull, dibsCss.hFull)}
                role="button"
                tabIndex={0}
                onClick={onClose}
                onKeyDown={() => {}}
                aria-label={intl.formatMessage(messages.close)}
            />
        </div>
    );
};
